// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene - AI/Tech theme with blue/purple accents
export const SCENE_COLORS = {
  scene1: { primaryColor: '#3B82F6', accentColor: '#ffffff' },   // Blue/White - Tech, ChatGPT
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Positive (5 businesses)
  scene3: { primaryColor: '#EF4444', accentColor: '#FCD34D' },   // Red/Gold - Alarming (97%)
  scene4: { primaryColor: '#FFFFFF', accentColor: '#EF4444' },   // White/Red - Muted, concerning
  scene5: { primaryColor: '#8B5CF6', accentColor: '#ffffff' },   // Purple/White - Solution
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA Premium
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },     // White/Gold - Brand
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/gdm-invisible-test/scene1-chatgpt.png',
  scene2: 'images/gdm-invisible-test/scene2-results.png',
  scene3: 'images/gdm-invisible-test/scene3-invisible.png',
  scene4: 'images/gdm-quiet-phone/scene1-quiet-phone.png',
  scene5: 'images/gdm-invisible-test/scene5-analytics.png',
  scene6: 'images/gdm-invisible-test/scene6-report.png',
};

// Hg market report presentation slides (reuse from quiet phone)
export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

// Dramatic words for "The Invisible Test" script
export const DRAMATIC_WORDS = ['97%', 'invisible.', 'Completely', 'Free', 'Report.'];

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
// Voice: Mark (UgBBYS2sOqTuMpoF3BR0) - American male
// Manual corrections: merged "97" + "%" into "97%", normalized punctuation/casing
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Hook - ChatGPT question
  { text: 'I', startMs: 0, endMs: 80 },
  { text: 'asked', startMs: 80, endMs: 460 },
  { text: 'ChatGPT:', startMs: 460, endMs: 1480 },
  { text: 'best', startMs: 1660, endMs: 2460 },
  { text: 'garage', startMs: 2460, endMs: 2880 },
  { text: 'door', startMs: 2880, endMs: 3200 },
  { text: 'company', startMs: 3200, endMs: 3520 },
  { text: 'near', startMs: 3520, endMs: 3820 },
  { text: 'me.', startMs: 3820, endMs: 4200 },

  // Scene 2: Result
  { text: 'It', startMs: 4200, endMs: 4520 },
  { text: 'recommended', startMs: 4520, endMs: 4880 },
  { text: '5', startMs: 4880, endMs: 5340 },
  { text: 'businesses.', startMs: 5340, endMs: 6460 },

  // Scene 3: Stat - 97% invisible (merged "97" + "%")
  { text: '97%', startMs: 6460, endMs: 8320 },
  { text: 'of', startMs: 8320, endMs: 8680 },
  { text: 'garage', startMs: 8680, endMs: 8900 },
  { text: 'door', startMs: 8900, endMs: 9140 },
  { text: 'companies?', startMs: 9140, endMs: 9740 },
  { text: 'Completely', startMs: 9740, endMs: 10440 },
  { text: 'invisible.', startMs: 10440, endMs: 11440 },

  // Scene 4: Pain points
  { text: 'Not', startMs: 11440, endMs: 12400 },
  { text: 'recommended.', startMs: 12400, endMs: 13020 },
  { text: 'Not', startMs: 13020, endMs: 13540 },
  { text: 'mentioned.', startMs: 13540, endMs: 14080 },
  { text: 'Not', startMs: 14080, endMs: 14620 },
  { text: 'getting', startMs: 14620, endMs: 14800 },
  { text: 'those', startMs: 14800, endMs: 15020 },
  { text: 'calls.', startMs: 15020, endMs: 15620 },

  // Scene 5: Solution
  { text: "We'll", startMs: 15620, endMs: 16100 },
  { text: 'show', startMs: 16100, endMs: 16240 },
  { text: 'you', startMs: 16240, endMs: 16360 },
  { text: 'exactly', startMs: 16360, endMs: 16620 },
  { text: 'where', startMs: 16620, endMs: 16900 },
  { text: 'you', startMs: 16900, endMs: 16980 },
  { text: 'stand.', startMs: 16980, endMs: 17760 },
  { text: 'Your', startMs: 17760, endMs: 18560 },
  { text: 'AI', startMs: 18560, endMs: 18760 },
  { text: 'visibility.', startMs: 18760, endMs: 19460 },
  { text: 'Your', startMs: 19460, endMs: 19660 },
  { text: 'competitors.', startMs: 19660, endMs: 20340 },
  { text: 'The', startMs: 20340, endMs: 20420 },
  { text: 'gaps.', startMs: 20420, endMs: 20980 },

  // Scene 6: CTA
  { text: 'Free', startMs: 20980, endMs: 21620 },
  { text: 'Market', startMs: 21620, endMs: 21880 },
  { text: 'Report.', startMs: 21880, endMs: 22440 },
  { text: 'Ten', startMs: 22440, endMs: 22680 },
  { text: 'minutes.', startMs: 22680, endMs: 23160 },
  { text: 'Link', startMs: 23160, endMs: 23440 },
  { text: 'below.', startMs: 23440, endMs: 23900 },
];

// Scene definitions with word groupings (aligned to Whisper timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 4200,
    words: WORD_TIMINGS.slice(0, 9),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Result',
    startMs: 4200,
    endMs: 6460,
    words: WORD_TIMINGS.slice(9, 13),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Stat',
    startMs: 6460,
    endMs: 11440,
    words: WORD_TIMINGS.slice(13, 20),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 11440,
    endMs: 15620,
    words: WORD_TIMINGS.slice(20, 28),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Solution',
    startMs: 15620,
    endMs: 20980,
    words: WORD_TIMINGS.slice(28, 42),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 20980,
    endMs: 23900,
    words: WORD_TIMINGS.slice(42),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - full animated logo playback
export const LOGO_SCENE = {
  startMs: 23900,
  endMs: 29000, // 5.1s logo duration
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
